import { message, Table } from 'antd'
import React, { useState } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import dayjs from 'dayjs'

import {
  IProductGroup,
  IProductGroupProduct,
} from '../../../types/product-group'
import { IFilterMapped } from './ProductGroupFilter'
import CircularLoading from '../../../components/CircularProgress'
import { ONLY_DATE_FORMAT, ONLY_TIME_FORMAT } from '../../../constants/global'
import Pagination from '../../../components/Pagination'
import ConfirmModal from '../../../components/ConfirmModal'
import { axiosInstance } from '../../../hooks/useAxios'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { ProductGroupFormModal } from './ProductGroupFormModal'
import ProductGroupViewModal from './ProductGroupViewModal'

interface IProductGroupsTableProps {
  isLoading: boolean
  data: IProductGroup[]
  filter: IFilterMapped
  handlePageChange: (filter: IFilterMapped) => void
  onUpdateSuccess: () => void
  total: number
}

const ProductGroupsTable = ({
  isLoading,
  data,
  filter,
  handlePageChange,
  onUpdateSuccess,
  total,
}: IProductGroupsTableProps) => {
  // แยก open ออกจากกลุ่มที่เลือก ให้หน้าต่างยังแสดงข้อมูลเดิมระหว่างปิด ไม่กระพริบ
  const [selected, setSelected] = useState<IProductGroup>()
  const [openDialog, setOpenDialog] = useState<'view' | 'edit' | 'delete'>()
  const openFor = (
    dialog: 'view' | 'edit' | 'delete',
    group: IProductGroup,
  ) => {
    setSelected(group)
    setOpenDialog(dialog)
  }
  const closeDialog = () => setOpenDialog(undefined)
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)

  const onDelete = async (group: IProductGroup) => {
    setIsLoadingDelete(true)
    try {
      await axiosInstance.delete(`/product-group/${group.objectId}`)
      message.success('ลบกลุ่มสินค้าสำเร็จ')
      onUpdateSuccess()
    } catch (e: any) {
      message.error(
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'ลบข้อมูลไม่สำเร็จ',
      )
    }
    setIsLoadingDelete(false)
  }

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => (
        <Typography variant="subtitle1" color="text.secondary">
          {numberRow}
        </Typography>
      ),
    },
    {
      title: 'ชื่อกลุ่มสินค้า',
      dataIndex: 'name',
      key: 'name',
      align: 'left' as const,
      render: (name: string) => (
        <Typography variant="h6" color="text.secondary">
          {name}
        </Typography>
      ),
    },
    {
      title: 'จำนวนสินค้า',
      dataIndex: 'products',
      key: 'products',
      align: 'center' as const,
      width: 140,
      render: (products: IProductGroupProduct[]) => (
        <Typography variant="subtitle1" color="text.secondary">
          {products?.length ?? 0} รายการ
        </Typography>
      ),
    },
    {
      title: 'วันที่เพิ่ม',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center' as const,
      render: (createdAt: string) => (
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'จัดการ',
      key: 'actions',
      width: 150,
      align: 'center' as const,
      render: (_: undefined, group: IProductGroup) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Tooltip title="ดูสินค้าในกลุ่ม">
            <IconButton onClick={() => openFor('view', group)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="แก้ไข">
            <IconButton onClick={() => openFor('edit', group)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="ลบ">
            <IconButton onClick={() => openFor('delete', group)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <>
      <ProductGroupViewModal
        isOpen={openDialog === 'view'}
        groupId={selected?.objectId}
        onClose={closeDialog}
      />
      <ProductGroupFormModal
        isOpen={openDialog === 'edit'}
        groupId={selected?.objectId}
        onClose={closeDialog}
        onSaveSuccess={onUpdateSuccess}
      />
      <ConfirmModal
        open={openDialog === 'delete'}
        title={'ลบกลุ่มสินค้า'}
        message={`คุณต้องการลบกลุ่มสินค้า ${selected?.name ?? ''} ใช่หรือไม่?`}
        onClose={closeDialog}
        onConfirm={() => {
          onDelete(selected!)
          closeDialog()
        }}
      />
      <CircularLoading isLoading={isLoading || isLoadingDelete} />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
        mb={2}
      />
      <Table
        size={'middle'}
        columns={columns}
        dataSource={data?.map((group: IProductGroup, index: number) => ({
          ...group,
          key: group.objectId,
          numberRow:
            index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
        }))}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default ProductGroupsTable
